import { useCallback, useEffect, useRef, useState } from 'react';
import { useCompany } from '@/company/context';
import { connectionApi, connectionKey } from './connection-api';
import type {
  ScopedConnectionItem as CompanyConnectionItem,
  ScopedConnections as CompanyConnections,
} from './connection-api';
import { connectionAttemptId, readConnectionReturn } from './connection-return-state';
import { completedConnection, connectionCompletionNotice } from './connection-completion';
import {
  updateConnectionPolicy,
  type ConnectionReadItem,
  type ConnectionAccessChoice,
} from './connection-read-access/api';
import { afterConnect, type PendingChoice } from './connect-access/model';
import { useIntlLocale } from './intl-locale';
import { formatCopy, namespaceCopyFor } from '@/i18n/ns';
import connectionsActionsCopy from '@/i18n/ns/connections-actions';

// The authorization happens on the credential plane's page in a popup. While
// it is open the app asks the server to reconcile; the server adopts only what
// the credential plane reports, so closing the popup early changes nothing.
const RECONCILE_EVERY_MS = 3000;
const RECONCILE_FOR_MS = 5 * 60 * 1000;

export function useConnections(companyName: string) {
  const company = useCompany();
  const locale = useIntlLocale();
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const scope = JSON.stringify([company.data?.company?.id, company.data?.membership?.userId]);
  const [snapshot, setSnapshot] = useState<{ scope: string; value: CompanyConnections } | null>(
    null,
  );
  const data = snapshot?.scope === scope ? snapshot.value : null;
  const setData = (value: CompanyConnections) => setSnapshot({ scope, value });
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [failure, setFailure] = useState<{ item: CompanyConnectionItem; detail: string } | null>(
    null,
  );
  // Set only when the browser blocked the popup: the user opens the link.
  const [connectLink, setConnectLink] = useState('');
  // The connect sheet (DES3 connect-access §1): the item it was opened for, or null.
  const [customerApp, setCustomerApp] = useState<CompanyConnectionItem | null>(null);
  const [sheet, setSheet] = useState<CompanyConnectionItem | null>(null);
  // The sheet's choice, applied only to the exact account this connect adds.
  const pending = useRef<PendingChoice | null>(null);
  const popup = useRef<Window | null>(null);
  const timer = useRef<number | null>(null);
  const generation = useRef(0);

  const stopPolling = useCallback(() => {
    generation.current += 1;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);

  // The read waits for the committed account and company: sent earlier (a deep link mounts this page
  // before the company bootstrap answers) it goes out before the session guard exists and without the pair.
  const resolved = Boolean(company.data?.company?.id && company.data?.membership?.userId);
  useEffect(() => {
    if (!resolved) return;
    let active = true;
    setError('');
    setBusy(null);
    setOpen(null);
    setNotice('');
    setFailure(null);
    setCustomerApp(null);
    setConnectLink('');
    // Read the actual service availability first. An unconfigured credential
    // plane has a catalogue but no reconcile route. Configured services still
    // reconcile on open so provider state decides what is connected or gone.
    void connectionApi
      .list()
      .then((value) => {
        if (!active || value.setup.reason === 'connector_service_not_configured') return value;
        return connectionApi.reconcile().catch(() => (active ? connectionApi.list() : value));
      })
      .then((value) => {
        if (active) setData(value);
      })
      .catch((failure) => {
        if (active)
          setError(
            failure instanceof Error
              ? failure.message
              : namespaceCopyFor(connectionsActionsCopy, localeRef.current).loadError,
          );
      });
    return () => {
      active = false;
    };
  }, [attempt, scope, resolved]);
  useEffect(() => stopPolling, [stopPolling, scope]);

  const pollUntilConnected = useCallback(
    (
      requested: CompanyConnectionItem,
      previousIds: Set<string | null>,
      attemptId: string,
      startedAt: number,
    ) => {
      stopPolling();
      const owner = generation.current;
      let closedAt: number | null = null;
      let reconcileFailed = false;
      let returnOutcome: 'complete' | 'failed' | null = null;
      const tick = async () => {
        if (owner !== generation.current) return;
        const closed = popup.current?.closed ?? true;
        if (closed && closedAt === null) closedAt = Date.now();
        returnOutcome ??= readConnectionReturn(attemptId, startedAt);
        let done = false;
        try {
          const value = await connectionApi.reconcile();
          reconcileFailed = false;
          if (owner !== generation.current) return;
          setData(value);
          const item = completedConnection(requested, value.items, previousIds);
          if (item) {
            done = true;
            const choice = pending.current;
            pending.current = null;
            // Exactly one new account for this app and audience, or the choice is not applied.
            const added = value.items.filter(
              (entry) =>
                entry.status === 'connected' &&
                entry.id === requested.id &&
                entry.audience === requested.audience &&
                !previousIds.has(entry.connectionId),
            ).length;
            const next = afterConnect(choice, scope, item, previousIds, added);
            // The choice is saved only for this new account; an ambiguous result changes no grant.
            if (next.kind === 'save')
              await updateConnectionPolicy(next.read, next.choice)
                .then((saved) => {
                  if (owner === generation.current) setReadAccess(saved);
                })
                .catch(() => undefined);
            if (owner !== generation.current) return;
            setOpen(connectionKey(item));
            const detail = connectionCompletionNotice(item, localeRef.current);
            setNotice(detail);
            setFailure(item.status === 'connected' ? null : { item, detail });
          }
        } catch {
          reconcileFailed = true;
        }
        if (owner !== generation.current) return;
        const expired = Date.now() - startedAt > RECONCILE_FOR_MS;
        // A pass or two after the popup closes catches an authorization that
        // completed in the last interval; then stop waiting. Without a popup
        // (the browser blocked it) there is nothing to close: the user
        // authorizes in another tab, and the page keeps checking until the
        // connection shows up or the window expires.
        const closedLongEnough =
          popup.current !== null && closedAt !== null && Date.now() - closedAt > RECONCILE_EVERY_MS;
        if (done || expired || closedLongEnough) {
          timer.current = null;
          pending.current = null;
          setBusy(null);
          setConnectLink('');
          if (!done) {
            const currentCopy = namespaceCopyFor(connectionsActionsCopy, localeRef.current);
            const detail = reconcileFailed
              ? currentCopy.providerCheckFailed
              : returnOutcome === 'failed'
                ? formatCopy(currentCopy.providerAuthorizationFailed, { name: requested.name })
                : returnOutcome === 'complete'
                  ? formatCopy(currentCopy.providerReturnedMissing, { name: requested.name })
                  : formatCopy(currentCopy.notConnected, { name: requested.name });
            setNotice(detail);
            setFailure({ item: requested, detail });
          }
          return;
        }
        timer.current = window.setTimeout(() => void tick(), RECONCILE_EVERY_MS);
      };
      timer.current = window.setTimeout(() => void tick(), RECONCILE_EVERY_MS);
    },
    [stopPolling, scope],
  );

  // The one place a connect starts. Company accounts and apps with reviewed reads go through the
  // connect sheet first (it replaces the old confirm); anything else connects at once.
  const requestConnect = (item: CompanyConnectionItem) => {
    if (busy || !item.actions.connect.available) return;
    if (item.appSetup) return setCustomerApp(item);
    setSheet(item);
  };

  // Continue in the sheet: the provider window opens in this same click.
  const continueConnect = (target: CompanyConnectionItem, choice: ConnectionAccessChoice) => {
    setSheet(null);
    setCustomerApp(null);
    void connect(target, { scope, appId: target.id, audience: target.audience, choice });
  };

  // Every connect carries its own choice, or none: an earlier one never carries over.
  const connect = async (item: CompanyConnectionItem, choice: PendingChoice | null = null) => {
    if (busy || !item.actions.connect.available) return;
    pending.current = choice;
    stopPolling();
    const owner = generation.current;
    const previousIds = new Set(data?.items.map((entry) => entry.connectionId) ?? []);
    const attemptId = connectionAttemptId();
    const startedAt = Date.now();
    setBusy(connectionKey(item));
    setNotice('');
    setFailure(null);
    setCustomerApp(null);
    setConnectLink('');
    // Open synchronously in the click so the browser treats it as user-initiated.
    popup.current = window.open('', 'poneva-connect', 'popup,width=640,height=760');
    const opened = popup.current;
    try {
      const session = await connectionApi.session(item, attemptId);
      if (owner !== generation.current) {
        opened?.close();
        return;
      }
      // The provider's page must not be able to navigate the workspace tab.
      if (popup.current) popup.current.opener = null;
      if (popup.current) popup.current.location.href = session.connectLink;
      else
        popup.current = window.open(
          session.connectLink,
          'poneva-connect',
          'popup,width=640,height=760',
        );
      if (!popup.current) setConnectLink(session.connectLink);
      pollUntilConnected(item, previousIds, attemptId, startedAt);
    } catch (failure) {
      if (owner !== generation.current) return;
      pending.current = null;
      popup.current?.close();
      setBusy(null);
      const detail =
        failure instanceof Error
          ? failure.message
          : namespaceCopyFor(connectionsActionsCopy, localeRef.current).startError;
      setNotice(detail);
      setFailure({ item, detail });
    }
  };

  const disconnect = async (item: CompanyConnectionItem, confirmed = false) => {
    if (busy || !item.connectionId || !item.actions.disconnect.available) return;
    if (
      !confirmed &&
      !window.confirm(
        formatCopy(
          item.audience === 'private'
            ? namespaceCopyFor(connectionsActionsCopy, localeRef.current).disconnectPrivate
            : namespaceCopyFor(connectionsActionsCopy, localeRef.current).disconnectCompany,
          { name: item.name, company: companyName },
        ),
      )
    )
      return;
    const owner = generation.current;
    setBusy(connectionKey(item));
    setNotice('');
    setFailure(null);
    try {
      await connectionApi.disconnect(item.connectionId);
      if (owner !== generation.current) return;
      const value = await connectionApi.list();
      if (owner !== generation.current) return;
      setData(value);
      setOpen(null);
      setNotice(
        formatCopy(namespaceCopyFor(connectionsActionsCopy, localeRef.current).disconnected, {
          name: item.name,
        }),
      );
    } catch (failure) {
      if (owner !== generation.current) return;
      setNotice(
        failure instanceof Error
          ? failure.message
          : namespaceCopyFor(connectionsActionsCopy, localeRef.current).disconnectError,
      );
      if (confirmed) throw failure;
    } finally {
      if (owner === generation.current) setBusy(null);
    }
  };

  // One click per card: connect what can be connected, expand what is
  // connected, and explain what cannot be set up here.
  const activate = (item: CompanyConnectionItem) => {
    if (busy) return;
    if (item.actions.connect.available) return requestConnect(item);
    setOpen((current) => (current === connectionKey(item) ? null : connectionKey(item)));
  };

  const cancel = () => {
    pending.current = null;
    stopPolling();
    setBusy(null);
    setConnectLink('');
    setNotice(namespaceCopyFor(connectionsActionsCopy, localeRef.current).stoppedWaiting);
  };
  // A saved reading for one connected account, as the server answered it.
  function setReadAccess(read: ConnectionReadItem) {
    setSnapshot((current) =>
      current?.scope === scope
        ? {
            scope,
            value: {
              ...current.value,
              items: current.value.items.map((entry) =>
                (entry.mcpConnectionId ?? entry.connectionId) === read.connectionId
                  ? { ...entry, readAccess: read }
                  : entry,
              ),
            },
          }
        : current,
    );
  }

  const setup = data?.pipedreamAvailable ? (data.pipedreamSetup ?? data.setup) : data?.setup;
  const companySetup = data?.pipedreamCompanySetup ?? data?.companySetup;
  return {
    data,
    error,
    sheet,
    customerApp,
    closeCustomerApp: () => setCustomerApp(null),
    customerAppAudiences: {
      canPrivate: Boolean(data?.setup.available),
      canCompany: Boolean(data?.companySetup.available),
    },
    sheetAudiences: {
      canPrivate: Boolean(setup?.available),
      canCompany: Boolean(companySetup?.available),
    },
    continueConnect,
    cancelSheet: () => {
      pending.current = null;
      setSheet(null);
    },
    requestConnect,
    setReadAccess,
    busy,
    open,
    notice,
    failure,
    dismissFailure: () => setFailure(null),
    connectLink,
    disconnect,
    activate,
    cancel,
    retry: () => setAttempt((value) => value + 1),
  };
}
